// User identity pill + popover, modelled on the Yehuda sidebar pattern.
// Renders a compact button (avatar + name/email) at the sidebar footer;
// clicking opens a popover above it with profile link + sign-out.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, LogOut, User as UserIcon } from "lucide-react";

interface UserMenuProps {
  user: {
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  /** When impersonating, the real admin's identity for display in the popover. */
  realUser?: { email: string; first_name: string | null; last_name: string | null } | null;
  collapsed?: boolean;
  onLogout: () => void;
  onExitImpersonation?: () => void;
}

function UserAvatar({ user, size = 22 }: { user: UserMenuProps["user"]; size?: number }) {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "";
  const initial = (name.charAt(0) || "?").toUpperCase();
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.5)) }}
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold"
    >
      {initial}
    </span>
  );
}

export function UserMenu({ user, realUser, collapsed = false, onLogout, onExitImpersonation }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  // Fixed-position coords measured from the trigger, so the popover escapes the
  // sidebar's `overflow-hidden` clip and pops OUT over the main content -- it
  // must not be constrained inside the (collapsed) rail.
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    function reposition() {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setPos({ left: r.left, bottom: window.innerHeight - r.top + 6 });
    }
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "";

  return (
    <div className="relative">
      {open && pos && (
        <div
          ref={popoverRef}
          style={{ left: pos.left, bottom: pos.bottom }}
          className="fixed w-56 overflow-hidden rounded-md border border-border bg-background shadow-lg z-50"
        >
          {realUser && onExitImpersonation && (
            <div className="border-b border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-[10px] font-medium text-amber-700">Real account</p>
              <p className="truncate text-[10px] text-amber-600">
                {[realUser.first_name, realUser.last_name].filter(Boolean).join(" ") || realUser.email}
              </p>
              <button
                type="button"
                onClick={() => { setOpen(false); onExitImpersonation(); }}
                className="mt-1 flex items-center gap-1 text-[10px] text-amber-700 hover:underline"
              >
                <LogOut size={10} />
                Exit impersonation
              </button>
            </div>
          )}
          <div className="flex items-center gap-3 border-b border-border px-3 py-3">
            <UserAvatar user={user} size={32} />
            <div className="min-w-0">
              {displayName && displayName !== user?.email && (
                <p className="truncate text-xs font-medium">{displayName}</p>
              )}
              {user?.email && (
                <p className="truncate text-[10px] text-muted-foreground">{user.email}</p>
              )}
            </div>
          </div>
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-accent/50 transition-colors"
            >
              <UserIcon size={12} className="text-muted-foreground" />
              My Profile
            </Link>
            <Link
              to="/preferences"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-accent/50 transition-colors"
            >
              <Settings size={12} className="text-muted-foreground" />
              Preferences
            </Link>
            <button
              type="button"
              onClick={() => { setOpen(false); onLogout(); }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-left text-muted-foreground hover:bg-accent/50 transition-colors"
            >
              <LogOut size={12} />
              Sign out
            </button>
          </div>
        </div>
      )}

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={collapsed ? displayName || "Account" : undefined}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted ${collapsed ? "justify-center" : ""}`}
      >
        <UserAvatar user={user} size={24} />
        {!collapsed && user?.email && (
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {displayName || user.email}
          </span>
        )}
      </button>
    </div>
  );
}
